import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api, { ApiError } from '@/lib/api';

type PageState =
  // Browser-redirect (port + state present)
  | { status: 'validating' }
  | { status: 'creating' }
  | { status: 'redirecting' }
  | { status: 'success' }
  | { status: 'error'; message: string }
  // Manual paste-code (no params)
  | { status: 'manual_idle' }
  | { status: 'manual_submitting' }
  | { status: 'manual_success' }
  | { status: 'manual_error'; message: string };

const CODE_ALPHABET_REGEX = /^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]*$/;

/** Strip dashes/whitespace and uppercase. Mirrors the server normalizer. */
function normalizeCode(input: string): string {
  return input.replace(/[\s-]/g, '').toUpperCase();
}

/** Display form: 'ABCDEFGH' → 'ABCD-EFGH'. */
function displayCode(input: string): string {
  const normalized = normalizeCode(input);
  if (normalized.length <= 4) return normalized;
  return `${normalized.slice(0, 4)}-${normalized.slice(4, 8)}`;
}

/**
 * CLI Auth Page — two modes.
 *
 * Mode A (browser redirect): URL has `?port=...&state=...`. The page
 * mints an API key via POST /api-keys and 302s to the CLI's localhost
 * callback. This is the existing flow used by `fias-dev login` on a
 * dev machine where localhost works.
 *
 * Mode B (manual paste-code): URL has no params. The page renders an
 * input box for an 8-char code that the user pastes from their CLI
 * terminal (Codespaces and similar environments where the CLI cannot
 * reach a localhost callback). Submitting calls
 * POST /cli-auth/code/:code/claim and the CLI's poll endpoint
 * delivers the API key.
 */
export function CliAuthPage() {
  const [searchParams] = useSearchParams();
  const port = searchParams.get('port');
  const stateParam = searchParams.get('state');
  const isRedirectMode = Boolean(port && stateParam);

  const [state, setState] = useState<PageState>(
    isRedirectMode ? { status: 'validating' } : { status: 'manual_idle' },
  );

  // ---------------------------------------------------------------------------
  // Mode A: browser redirect (existing behavior, unchanged)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!isRedirectMode) return;

    const portNum = parseInt(port!, 10);
    if (isNaN(portNum) || portNum < 1024 || portNum > 65535) {
      setState({ status: 'error', message: 'Invalid port number.' });
      return;
    }

    let cancelled = false;

    async function authorize() {
      setState({ status: 'creating' });

      try {
        const result = await api.createApiKey({
          name: 'FIAS CLI (auto-generated)',
          scopes: ['developer'],
        });

        if (cancelled) return;

        setState({ status: 'redirecting' });

        const callbackUrl = new URL(`http://localhost:${port}/callback`);
        callbackUrl.searchParams.set('key', result.secret);
        callbackUrl.searchParams.set('state', stateParam!);

        window.location.href = callbackUrl.toString();
      } catch (err) {
        if (cancelled) return;
        setState({
          status: 'error',
          message: err instanceof Error ? err.message : 'Failed to create API key',
        });
      }
    }

    authorize();

    return () => {
      cancelled = true;
    };
  }, [isRedirectMode, port, stateParam]);

  function handleRetryRedirect() {
    setState({ status: 'creating' });
    api
      .createApiKey({ name: 'FIAS CLI (auto-generated)', scopes: ['developer'] })
      .then((result) => {
        setState({ status: 'redirecting' });
        const callbackUrl = new URL(`http://localhost:${port}/callback`);
        callbackUrl.searchParams.set('key', result.secret);
        callbackUrl.searchParams.set('state', stateParam!);
        window.location.href = callbackUrl.toString();
      })
      .catch((err) => {
        setState({
          status: 'error',
          message: err instanceof Error ? err.message : 'Failed to create API key',
        });
      });
  }

  // ---------------------------------------------------------------------------
  // Mode B: manual paste-code
  // ---------------------------------------------------------------------------

  const [codeInput, setCodeInput] = useState('');

  function onCodeChange(e: React.ChangeEvent<HTMLInputElement>) {
    // Strip out anything outside the alphabet (besides dashes/whitespace,
    // which normalize away). This makes the input self-correcting.
    const stripped = e.target.value
      .replace(/[\s-]/g, '')
      .toUpperCase()
      .split('')
      .filter((ch) => CODE_ALPHABET_REGEX.test(ch))
      .slice(0, 8)
      .join('');
    setCodeInput(displayCode(stripped));
  }

  async function handleManualSubmit(e: React.FormEvent) {
    e.preventDefault();
    const normalized = normalizeCode(codeInput);
    if (normalized.length !== 8) {
      setState({
        status: 'manual_error',
        message: 'Code must be 8 characters (e.g. ABCD-EFGH).',
      });
      return;
    }

    setState({ status: 'manual_submitting' });
    try {
      await api.claimCliAuthCode(normalized);
      setState({ status: 'manual_success' });
    } catch (err) {
      const message = mapClaimError(err);
      setState({ status: 'manual_error', message });
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '80px' }}>
      <Card style={{ maxWidth: '480px', width: '100%' }}>
        <CardHeader>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>FIAS CLI Authorization</h2>
        </CardHeader>
        <CardContent>
          {/* Browser-redirect mode UI */}
          {(state.status === 'validating' || state.status === 'creating') && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  border: '3px solid #e5e7eb',
                  borderTopColor: '#3b82f6',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                  margin: '0 auto 16px',
                }}
              />
              <p>Creating API key for FIAS CLI...</p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {state.status === 'redirecting' && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <p>Redirecting to CLI...</p>
            </div>
          )}

          {state.status === 'success' && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <p style={{ color: '#22c55e', fontWeight: 600, fontSize: '1.1rem' }}>
                Authentication successful!
              </p>
              <p style={{ marginTop: '8px', color: '#6b7280' }}>
                You can close this tab and return to your terminal.
              </p>
            </div>
          )}

          {state.status === 'error' && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <p style={{ color: '#ef4444', fontWeight: 600 }}>Authorization failed</p>
              <p style={{ marginTop: '8px', color: '#6b7280' }}>{state.message}</p>
              {port && stateParam && (
                <Button onClick={handleRetryRedirect} style={{ marginTop: '16px' }}>
                  Try again
                </Button>
              )}
            </div>
          )}

          {/* Manual paste-code mode UI */}
          {(state.status === 'manual_idle' ||
            state.status === 'manual_submitting' ||
            state.status === 'manual_error') && (
            <form
              onSubmit={handleManualSubmit}
              style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>
                Enter the code shown in your terminal to authorize the CLI.
              </p>
              <div>
                <Label htmlFor="cli-auth-code">Code</Label>
                <Input
                  id="cli-auth-code"
                  type="text"
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  placeholder="ABCD-EFGH"
                  value={codeInput}
                  onChange={onCodeChange}
                  disabled={state.status === 'manual_submitting'}
                  style={{
                    fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                    letterSpacing: '0.05em',
                  }}
                />
              </div>
              {state.status === 'manual_error' && (
                <p style={{ color: '#ef4444', fontSize: '0.9rem' }}>{state.message}</p>
              )}
              <Button type="submit" disabled={state.status === 'manual_submitting'}>
                {state.status === 'manual_submitting' ? 'Authorizing…' : 'Authorize CLI'}
              </Button>
            </form>
          )}

          {state.status === 'manual_success' && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <p style={{ color: '#22c55e', fontWeight: 600, fontSize: '1.1rem' }}>
                CLI authorized!
              </p>
              <p style={{ marginTop: '8px', color: '#6b7280' }}>
                Return to your terminal — your CLI will pick up the credentials shortly.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function mapClaimError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 404)
      return 'Code not found. Run `fias-dev login --manual` to mint a new one.';
    if (err.status === 409)
      return 'This code has already been used. Run `fias-dev login --manual` again.';
    if (err.status === 410)
      return 'This code has expired. Run `fias-dev login --manual` to mint a new one.';
    if (err.status === 429) return 'Too many attempts. Wait a moment and try again.';
    if (err.status === 400) return 'Invalid code format. Codes look like ABCD-EFGH.';
    return err.message || 'Failed to authorize CLI.';
  }
  return err instanceof Error ? err.message : 'Failed to authorize CLI.';
}
